<template>
  <div class="search-form-section">
    <div class="container">
      <div class="search-form pt-3 rtl-flex-d-row-r">
        <div class="alternative-search-options mx-2">
          <div class="dropdown">
            <button 
              class="btn btn-outline-secondary dropdown-toggle lang-toggle" 
              id="langSwitcher" 
              role="button" 
              data-bs-toggle="dropdown" 
              aria-expanded="false"
            >
              <i class="fa-solid fa-globe"></i>
            </button>
            <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="langSwitcher">
              <li><a class="dropdown-item" href="#" @click.prevent="switchLanguage('fa')" :class="{active:locale==='fa'}"><i class="fa-solid fa-check me-2" v-if="locale==='fa'"></i> {{ $t('common.lang_fa') }}</a></li>
              <li><a class="dropdown-item" href="#" @click.prevent="switchLanguage('en')" :class="{active:locale==='en'}"><i class="fa-solid fa-check me-2" v-if="locale==='en'"></i> {{ $t('common.lang_en') }}</a></li>
              <li><a class="dropdown-item" href="#" @click.prevent="switchLanguage('ar')" :class="{active:locale==='ar'}"><i class="fa-solid fa-check me-2" v-if="locale==='ar'"></i> {{ $t('common.lang_ar') }}</a></li>
              <li><a class="dropdown-item" href="#" @click.prevent="switchLanguage('ru')" :class="{active:locale==='ru'}"><i class="fa-solid fa-check me-2" v-if="locale==='ru'"></i> {{ $t('common.lang_ru') }}</a></li>
              <li><a class="dropdown-item" href="#" @click.prevent="switchLanguage('tr')" :class="{active:locale==='tr'}"><i class="fa-solid fa-check me-2" v-if="locale==='tr'"></i> {{ $t('common.lang_tr') }}</a></li>
            </ul>
          </div>
        </div>
        <div class="search-wrapper">
          <form @submit.prevent="submitForm">
            <button type="submit">
              <i class="fa-solid fa-magnifying-glass"></i>
            </button>
            <input 
              class="form-control" 
              v-model="searchQuery" 
              type="search" 
              :placeholder="$t('home.search_placeholder')"
              @focus="onFocus"
              @blur="onBlur"
            >
          </form>
          <ul v-if="showDropdown" class="search-dropdown">
            <li v-for="(item, index) in searchResults" :key="index" @mousedown.prevent="selectCategory(item)">
              {{ item.label }}
            </li>
            <li v-if="searchResults.length === 0" class="no-results">
              {{ $t('common.no_results') }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const { locale, setLocale } = useI18n()
const router = useRouter()
const localePath = useLocalePath()

const searchQuery = ref('')
const searchResults = ref([])
const showDropdown = ref(false)
const focused = ref(false)
let searchTimeout = null

const submitForm = () => {
  const q = searchQuery.value.trim()
  if (q) {
    showDropdown.value = false
    router.push(localePath(`/search/${encodeURIComponent(q)}`))
  }
}

const switchLanguage = async (lang) => {
  await setLocale(lang)
}

const onFocus = () => {
  focused.value = true
  if (searchResults.value.length > 0) {
    showDropdown.value = true
  }
}

const onBlur = () => {
  setTimeout(() => {
    focused.value = false
    showDropdown.value = false
  }, 200)
}

const selectCategory = (item) => {
  showDropdown.value = false
  searchQuery.value = ''
  searchResults.value = []
  router.push(localePath(`/cat/${item.value}/${encodeURIComponent(item.label)}`))
}

watch(searchQuery, (val) => {
  if (searchTimeout) clearTimeout(searchTimeout)

  if (val.trim().length < 3) {
    searchResults.value = []
    showDropdown.value = false
    return
  }

  searchTimeout = setTimeout(async () => {
    try {
      const res = await $fetch('https://sarvban.com/app/api/anar/search-category/', {
        method: 'POST',
        body: { word: val.trim() }
      })
      const data = typeof res === 'string' ? JSON.parse(res) : res
      searchResults.value = data
      showDropdown.value = focused.value && data.length > 0
    } catch (e) {
      console.error('Search category error:', e)
      searchResults.value = []
      showDropdown.value = false
    }
  }, 300)
})
</script>

<style lang="scss" scoped>
.search-form-section {
  .search-form {
    display: flex;
    align-items: center;
    
    form {
      display: flex;
      flex: 1;
      
      input {
        border-radius: 8px 0 0 8px;
        border: none;
        background: #f5f5f5;
        padding: 10px 20px;
        font-size: 14px;
        
        &::placeholder {
          color: #999;
        }
        
        &:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(3, 160, 84, 0.2);
        }
      }
      
      button {
        margin-right: 5px;
        border: none;
        background: #03a054;
        color: white;
        border-radius:0% 8px 8px 0%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background-color 0.3s;
        
        &:hover {
          background-color: #028a46;
        }
      }
    }

    .search-wrapper {
      position: relative;
      flex: 1;
      display: flex;
      flex-direction: column;

      .search-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 0 0 8px 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        list-style: none;
        padding: 0;
        margin: 0;
        z-index: 200;
        max-height: 300px;
        overflow-y: auto;

        li {
          padding: 10px 16px;
          cursor: pointer;
          font-size: 14px;
          color: #333;
          border-bottom: 1px solid #f0f0f0;
          transition: background-color 0.15s;

          &:hover {
            background: #e8f5e9;
            color: #03a054;
          }

          &:last-child {
            border-bottom: none;
          }

          &.no-results {
            color: #999;
            cursor: default;

            &:hover {
              background: transparent;
              color: #999;
            }
          }
        }
      }
    }
    
    .alternative-search-options {
      .lang-toggle {
        border: 1px solid #dee2e6;
        background: white;
        color: #555;
        border-radius: 8px;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        font-size: 16px;
        
        &::after {
          display: none;
        }
        
        &:hover {
          background: #f5f5f5;
          border-color: #03a054;
        }
      }
      
      .dropdown-item.active {
        background: #e8f5e9;
        color: #03a054;
        font-weight: 600;
      }
    }
  }
}
</style>